import { useState } from 'react';
import { Plus, Check, X, BookOpen, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';

/**
 * CitationPicker - Modal for selecting or creating citations to insert
 * 
 * Features:
 * - List existing citations with click-to-insert
 * - Create new citation inline
 * - Shows citation preview with number
 */
export function CitationPicker({
  open,
  onOpenChange,
  citations = [],
  onSelectCitation,
  onAddCitation
}) {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newCitationText, setNewCitationText] = useState('');
  const [newCitationUrl, setNewCitationUrl] = useState('');

  const handleSelectCitation = (index) => {
    onSelectCitation(index);
    onOpenChange(false);
  };

  const handleAddNewCitation = () => {
    if (newCitationText.trim()) {
      // Create citation HTML with optional link
      let citationHtml = newCitationText.trim();
      if (newCitationUrl.trim()) {
        citationHtml = `<a href="${newCitationUrl.trim()}" target="_blank" rel="noopener noreferrer">${citationHtml}</a>`;
      }
      
      onAddCitation(citationHtml);
      setNewCitationText('');
      setNewCitationUrl('');
      setIsAddingNew(false);
    }
  };

  const handleAddAndInsert = () => {
    if (newCitationText.trim()) {
      // Create citation HTML with optional link
      let citationHtml = newCitationText.trim();
      if (newCitationUrl.trim()) {
        citationHtml = `<a href="${newCitationUrl.trim()}" target="_blank" rel="noopener noreferrer">${citationHtml}</a>`;
      }
      
      const newIndex = citations.length;
      onAddCitation(citationHtml);
      onSelectCitation(newIndex);
      
      setNewCitationText('');
      setNewCitationUrl('');
      setIsAddingNew(false);
      onOpenChange(false);
    }
  };

  const cancelAddNew = () => {
    setIsAddingNew(false);
    setNewCitationText('');
    setNewCitationUrl('');
  };

  // Strip HTML tags for preview
  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-accent" />
            Insert Citation
          </DialogTitle>
          <DialogDescription>
            Select an existing reference or create a new one to insert at the cursor position.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {/* Existing Citations */}
          {citations.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Existing References</Label>
              <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2">
                {citations.map((citation, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSelectCitation(idx)}
                    className="w-full text-left p-3 rounded-md border border-border hover:border-accent hover:bg-accent/5 transition-colors group"
                    data-testid={`citation-option-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="shrink-0 w-6 h-6 bg-accent/10 text-accent rounded-full flex items-center justify-center text-xs font-semibold">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground line-clamp-2">
                          {stripHtml(citation)}
                        </p>
                      </div>
                      <Check className="h-4 w-4 text-accent opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Add New Citation */}
          {isAddingNew ? (
            <div className="space-y-3 p-4 bg-muted/30 rounded-lg border border-border">
              <Label className="text-sm font-medium">New Reference</Label>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Citation Text *</Label>
                  <Input
                    value={newCitationText}
                    onChange={(e) => setNewCitationText(e.target.value)}
                    placeholder="Author, Title, Publication, Year, etc."
                    className="text-sm"
                    autoFocus
                    data-testid="new-citation-text"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">
                    URL (optional)
                    <ExternalLink className="h-3 w-3 inline ml-1" />
                  </Label>
                  <Input
                    value={newCitationUrl}
                    onChange={(e) => setNewCitationUrl(e.target.value)}
                    placeholder="https://..."
                    className="text-sm"
                    data-testid="new-citation-url"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAddAndInsert}
                  disabled={!newCitationText.trim()}
                  data-testid="add-and-insert-citation"
                >
                  Add & Insert
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddNewCitation}
                  disabled={!newCitationText.trim()}
                >
                  Add Only
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={cancelAddNew}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setIsAddingNew(true)}
              data-testid="add-new-citation-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Reference
            </Button>
          )}

          {/* Empty State */}
          {citations.length === 0 && !isAddingNew && (
            <div className="text-center py-6 text-muted-foreground">
              <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No references yet.</p>
              <p className="text-xs">Click "Add New Reference" to create your first citation.</p>
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Tip: Citations are numbered automatically. The same reference can be cited multiple times.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CitationPicker;
